// src/components/organization/UsageMeter.jsx
// ✅ Legge direttamente da AuthContext — zero fetch duplicati
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PLANS } from '../../utils/constants';

const UsageMeter = () => {
    const { usage, planLimits, effectivePlan, isSuperAdmin, loading } = useAuth();

    if (loading || !usage || !planLimits) return null;

    const isUnlimited = (val) =>
        val === -1 || val === null || val === undefined || val === Infinity || val >= 999999;

    const metrics = [
        {
            key: 'projects', label: 'Progetti', icon: '🏗️', color: 'blue',
            current: usage.projectsCount || 0, limit: planLimits.maxProjects
        },
        {
            key: 'users', label: 'Utenti', icon: '👥', color: 'green',
            current: usage.usersCount || 0, limit: planLimits.maxUsers
        },
        {
            key: 'expenses', label: 'Spese', icon: '💰', color: 'purple',
            current: usage.expensesCount || 0, limit: planLimits.maxExpenses
        },
        {
            key: 'storage', label: 'Storage', icon: '💾', color: 'orange', unit: 'MB',
            current: usage.storageUsedMb || 0, limit: planLimits.maxStorageMb
        },
    ];

    const pct = (current, limit) =>
        isUnlimited(limit) ? 0 : Math.min(100, (current / limit) * 100);

    const barColor = (p, color) => {
        if (p >= 90) return 'bg-red-500';
        if (p >= 70) return 'bg-yellow-500';
        return { blue: 'bg-blue-500', green: 'bg-green-500', purple: 'bg-purple-500', orange: 'bg-orange-500' }[color] || 'bg-blue-500';
    };

    const limitReached = !isSuperAdmin &&
        metrics.some(m => !isUnlimited(m.limit) && m.current >= m.limit);

    const canUpgrade = effectivePlan !== PLANS.ENTERPRISE;

    return (
        <div className="bg-white rounded-2xl border p-6">
            <div className="flex justify-between items-center mb-5">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Utilizzo del Piano</h3>
                    {isSuperAdmin && (
                        <p className="text-xs text-yellow-600 mt-0.5">👑 SuperAdmin — nessun limite</p>
                    )}
                </div>
                {canUpgrade && !isSuperAdmin && (
                    <Link to="/settings?tab=plan" className="text-xs text-blue-600 hover:underline font-medium">
                        Upgrade →
                    </Link>
                )}
            </div>

            <div className="space-y-4">
                {metrics.map(m => {
                    const unlimited = isUnlimited(m.limit);
                    const p = pct(m.current, m.limit);
                    return (
                        <div key={m.key}>
                            <div className="flex justify-between items-center mb-1.5">
                                <div className="flex items-center gap-2">
                                    <span>{m.icon}</span>
                                    <span className="text-sm text-gray-600">{m.label}</span>
                                </div>
                                <span className={`text-xs font-semibold ${p >= 90 && !unlimited ? 'text-red-600' : unlimited ? 'text-emerald-600' : 'text-gray-700'}`}>
                                    {unlimited
                                        ? `${m.current} / ∞`
                                        : `${m.current} / ${m.limit}${m.unit ? ' ' + m.unit : ''}`
                                    }
                                </span>
                            </div>
                            {!unlimited && (
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full ${barColor(p, m.color)} transition-all duration-500`}
                                        style={{ width: `${p}%` }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {limitReached && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center justify-between gap-3">
                    <p className="text-xs font-medium text-yellow-800">
                        ⚠️ Limite del piano raggiunto
                    </p>
                    <Link to="/settings?tab=plan"
                        className="flex-shrink-0 px-3 py-1 bg-yellow-600 text-white text-xs font-semibold rounded-lg hover:bg-yellow-700 transition">
                        Upgrade
                    </Link>
                </div>
            )}
        </div>
    );
};

export default UsageMeter;